import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import type { ADNode } from '@atlaskit/editor-common/validator';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse';

import { FORGE_DOC_UPDATED_EVENT } from '@atlassian/xen-editor-provider';

import { expValEquals } from '@confluence/feature-experiments';

type AdfType = ADNode | string | null;

/**
 * Hook for Table of Contents macro that updates headings and links when the ADF
 * is modified on the frontend by a Forge app
 */
export const useCurrentAdf = (adf: AdfType, isInEditMode: boolean) => {
	const [currentAdf, setCurrentAdf] = useState<AdfType>(adf);

	useEffect(() => {
		if (
			!isInEditMode &&
			expValEquals('confluence_toc_macro_compatibility_with_apps', 'isEnabled', true)
		) {
			const onForgeDocUpdated = (event: Event) => handleDocUpdated(event, setCurrentAdf);
			document.addEventListener(FORGE_DOC_UPDATED_EVENT, onForgeDocUpdated);
			return () => document.removeEventListener(FORGE_DOC_UPDATED_EVENT, onForgeDocUpdated);
		}
	}, [isInEditMode]);

	return isInEditMode ||
		!expValEquals('confluence_toc_macro_compatibility_with_apps', 'isEnabled', true)
		? adf // Use adf in edit mode to keep TOC updated with user changes
		: currentAdf;
};

// Exported for testing only
export const handleDocUpdated = (event: Event, setAdf: Dispatch<SetStateAction<AdfType>>) => {
	const { forgeDoc, localId } = (event as CustomEvent).detail;

	setAdf((prevAdf) => {
		let adfObject = prevAdf as ADFEntity;
		if (typeof prevAdf === 'string') {
			try {
				adfObject = JSON.parse(prevAdf);
			} catch (err) {
				// Swallow the error as ADF format error handling is expected to be done earlier,
				// in the component that renders the entire content
				return prevAdf;
			}
		}

		const newAdf = traverse(adfObject, {
			// Match the triggering macro's localId in the ADF and update the macro content
			bodiedExtension: (node) => {
				if (node.attrs?.localId === localId) {
					const newContent = forgeDoc?.children?.[0]?.props?.document?.content;
					if (newContent) {
						node.content = newContent;
					}
					return node;
				}
			},
		});

		if (newAdf) {
			return newAdf as ADNode;
		}
		return prevAdf;
	});
};
